// packages:
import React from "react"
import { BrowserRouter as Router, Route, Link } from "react-router-dom"
import moment from "moment"
import numeral from "numeral"
import { Field, reduxForm } from "redux-form"
import styled, { css } from "styled-components"
// local:
import "./Trip.css"
import Card, { TitleCard } from "../core/Card"
import {
  ClockIcon,
  NoteIcon,
  CalendarIcon,
  CarIcon,
  Icon,
  BackIcon,
  FromToIcon,
  PlusIcon,
  CrownIcon,
  BriefcaseIcon,
  MarkerIcon,
  ForwardIcon,
  HomeIcon,
  DotIcon
} from "../core/icons"
import SelectLocation from "./SelectLocation"
import AutoComplete from "../map/AutoComplete"
import {
  Popover,
  TimePicker,
  Button,
  Switch,
  DatePicker,
  Select,
  Modal,
  Input,
  Icon as AIcon,
  Tooltip,
  Form,
  Alert,
  Popconfirm,
  Row,
  Col
} from "antd"
import Uploader from "../core/Uploader"
import ScrollToTopOnMount from "../core/ScrollToTopOnMount"
import Map from "../map/Map"

const FormItem = Form.Item
const { Option } = Select
const { TextArea } = Input

const EndedAt = styled(TimePicker)`
  display: inline-block;
  border-radius: 3px;
  padding: 0.5rem 0;
  margin: 0.5rem 1rem;
  width: 11rem;
  background: teal;
  color: orange;
  border: 2px solid blue;
  ${props =>
    props.primary &&
    css`
      background: lightblue;
      color: palevioletred;
    `};
`

const makeFieldMiles = Component => ({
  formLayout,
  input,
  meta,
  children,
  hasFeedback,
  label,
  ...rest
}) => {
  const hasError = meta.touched && meta.invalid
  const { value, ...newInput } = input

  newInput.value = value //&& numeral(value).format("0,0.0")

  return (
    <Tooltip visible={hasError} title={meta.error}>
      <Component
        style={hasError ? { border: "solid 1px red" } : null}
        // type={value && parseFloat(value) > 999 ? "text" : "number"} //allow native arrows but prevent type=number react regex field crash
        placeholder="0.0"
        {...newInput}
        {...rest}
        children={children}
      />
    </Tooltip>
  )
}

const makeField = Component => ({
  formLayout,
  input,
  meta,
  children,
  hasFeedback,
  label,
  ...rest
}) => {
  const hasError = meta.touched && meta.invalid
  const { value, ...newInput } = input
  if (value != "") newInput.value = value
  return <Component {...rest} {...newInput} children={children} />
}
const required = value => (value ? undefined : "Required")

const AInput = makeField(Input)
const AInputMiles = makeFieldMiles(Input)
const ASelect = makeField(Select)
const ASwitch = makeField(Switch)
const ATextArea = makeField(TextArea)

const minAmount = amount => (value, previousValue, allValues) =>
  parseFloat(value) >= parseFloat(amount) ? value : previousValue

const StartedAt = styled.div`
  display: inline-block;
  border-radius: 3px;
  padding: 0.5rem 0;
  margin: 0.5rem 1rem;
  cursor: not-allowed;
  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `};
`
// NOTE there is a bug that breaks re-rendering of certain props.
// TODO is it the router?
// for debugging, use something like:
/* <Button disabled={!touched}>touched</Button>
<Button disabled={!syncError}>syncError</Button>
<Button disabled={!dirty}>dirty</Button>
<Button disabled={!pristine}>pristine</Button>
<Button disabled={!anyTouched}>anyTouched</Button>
<Button disabled={!invalid}>invalid</Button> */

const Trip = ({
  onPolylineClick,
  hasOriginDestination,
  auto_detected,
  undoMerge,
  wasMerged,
  kms,
  kms_enabled,
  started_at,
  ended_at,
  photo,
  onUpload,
  date,
  onDateChange,
  vehicleOptions,
  purposeSelectValue,
  onSelectPurpose,
  trip,
  deduction,
  onDelete,
  error,
  pristine,
  submitting,
  title,
  allowCustomAddress,
  buttonTitle,
  purposes,
  save,
  toggleRoundTrip,
  toggleCalculateRoute,
  calculate_route_enabled,
  round_trip_enabled,
  isFetching,
  touched,
  syncError,
  dirty,
  anyTouched,
  invalid,
  nextTripId
}) => (
  <div className="Trip">
    <ScrollToTopOnMount />
    <TitleCard className="Title" showBackButton sticky>
      <h1>{title}</h1>
      {anyTouched && error && <Alert message={error} type="error" />}

      <Button
        loading={submitting}
        disabled={pristine}
        onClick={save}
        type="primary"
      >
        {buttonTitle}
      </Button>
      {onDelete && (
        <Tooltip title={<span>Delete</span>}>
          <Popconfirm
            title="Are you sure?"
            placement="left"
            onConfirm={onDelete}
            okText="Yes"
            cancelText="No"
          >
            <Button type="danger" icon="delete" />
          </Popconfirm>
        </Tooltip>
      )}
      {wasMerged && (
        <Tooltip title={<span>Un-merge</span>}>
          <Popconfirm
            title="Are you sure?"
            placement="left"
            onConfirm={undoMerge}
            okText="Yes"
            cancelText="No"
          >
            <Button type="danger" icon="link" />
          </Popconfirm>
        </Tooltip>
      )}

      {/* {nextTripId &&
      <Link to={nextTripId}>Next ></Link>
    } */}
    </TitleCard>
    <Row gutter={20}>
      <Col sm={10}>
        {isFetching ? (
          <Card>
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                height: "50vh"
              }}
            >
              <AIcon
                type="loading"
                style={{
                  fontSize: "60px",
                  color: "#31b59f",
                  fontWeight: "600"
                }}
              />
            </div>
          </Card>
        ) : (
          <Card className="Options" gridArea="Options">
            <div className="totals">
              <div>
                <Field
                  name={kms_enabled ? "kms" : "miles"}
                  component={AInputMiles}
                  normalize={minAmount(0)}
                  parse={value => numeral(value).value()}
                  format={value => numeral(value).format("0,0.0")}
                />
                {kms_enabled ? "Kilometres" : "Miles"}
              </div>
              <div>
                <div>{numeral(deduction).format("$0,0.00")}</div>Deduction
              </div>

              <Uploader
                photo={photo}
                beforeUpload={onUpload}
                tooltipTitle={`Upload a photo here (odometer reading, photo from your trip, etc.)`}
              />
            </div>

            <hr />

            <div>
              <CalendarIcon />
              <DatePicker
                value={moment(date, "MMM DD, YYYY")}
                format={"MM-DD-YYYY"}
                onChange={onDateChange}
                allowClear={false}
              />
            </div>

            {started_at && ended_at ? (
              <Row>
                <ClockIcon width="25" height="25" />

                <Tooltip
                  title={`${moment(ended_at).diff(
                    started_at,
                    "minutes"
                  )} minutes`}
                  placement="topLeft"
                >
                  <StartedAt disabled>
                    {moment(started_at, "YYYY-MM-DD HH:mm:ss ZZZ").format(
                      "h:mm a"
                    )}
                    {" - "}
                    {moment(ended_at, "YYYY-MM-DD HH:mm:ss ZZZ").format(
                      "h:mm a"
                    )}
                  </StartedAt>
                </Tooltip>
              </Row>
            ) : null}
            {/* <Row>
      <TimeIcon/>
      <EndedAt>Test</EndedAt>
      <EndedAt>Test</EndedAt>

    </Row> */}
            <Row>
              <DotIcon height={25} width={25} color="#31B59F" />

              {!trip && <AutoComplete allowCustomAddress from />}
              {trip &&
                trip.from && (
                  <AutoComplete
                    from
                    defaultValue={
                      trip.start_place ? trip.start_place.name : trip.from
                    }
                  />
                )}
            </Row>

            <Row>
              <DotIcon height={25} width={25} color="#FF5166" />

              {!trip && <AutoComplete allowCustomAddress to />}
              {trip &&
                trip.to && (
                  <AutoComplete
                    to
                    defaultValue={
                      trip.end_place ? trip.end_place.name : trip.to
                    }
                  />
                )}
            </Row>

            <Row>
              <Icon type={purposeSelectValue} selected width="25" height="25" />
              <Select
                defaultValue={"Work"}
                value={purposeSelectValue}
                onSelect={onSelectPurpose}
              >
                {purposes.map(purpose => (
                  <Option
                    key={purpose}
                    value={purpose}
                    title={"Purpose: " + purpose}
                  >
                    <Row type="flex" justify="space-between" align="middle">
                      <span>{purpose}</span>
                    </Row>
                  </Option>
                ))}
              </Select>
            </Row>

            <Row>
              <Icon width="25" height="25" type={"Car"} />
              {/* <code>{JSON.stringify( trip ? (vehicleOptions.find(v=>v.value==trip.vehicle_id).icon_name):'Car' )}</code> */}
              {/* <Icon type={vehicleOptions.find(v=>v.value==trip.vehicle_id).icon_name||"Car"}/> */}
              <Field
                name="vehicle_id"
                component={ASelect}
                notFoundContent="Please add a Vehicle in settings."
                placeholder="Choose vehicle"
                label="checking"
              >
                {vehicleOptions.map(v => (
                  <Option
                    key={v.value}
                    value={v.value}
                    title={"Vehicle: " + v.title}
                  >
                    <Row type="flex" justify="space-between" align="middle">
                      {v.title}
                    </Row>
                  </Option>
                ))}
              </Field>
            </Row>

            <Row style={{ alignItems: "flex-start" }}>
              <NoteIcon />
              <Field
                name="notes"
                component={ATextArea}
                placeholder="Notes..."
                style={{ marginLeft: "10px" }}
              />
            </Row>

            <hr />

            {toggleCalculateRoute &&
              auto_detected && (
                <div>
                  <Switch
                    onChange={toggleCalculateRoute}
                    checked={calculate_route_enabled}
                  />
                  Calculate Route
                  <Tooltip
                    title={`Calculate Route uses directions from Google Maps to create a route for you.
          This will create a new map or overrride an existing trip recorded by GPS.`}
                  >
                    <AIcon
                      style={{ marginLeft: "20px" }}
                      type="question-circle-o"
                    />
                  </Tooltip>
                </div>
              )}
            <div>
              <Switch
                onChange={toggleRoundTrip}
                checked={round_trip_enabled}
                disabled={!hasOriginDestination}
              />
              Round Trip
              <Tooltip
                title={
                  hasOriginDestination
                    ? `Round Trip will recalculate this route using Google Maps, which is why some trips appear as a loop.`
                    : `Please enter a valid origin and destination to activate`
                }
              >
                <AIcon
                  style={{ marginLeft: "20px" }}
                  type="question-circle-o"
                />
              </Tooltip>
            </div>
          </Card>
        )}
      </Col>

      <Col sm={14}>
        <Card className="Map" gridArea="Map">
          <Map onPolylineClick={onPolylineClick} />
        </Card>
      </Col>
    </Row>
  </div>
)

export default Trip



// WEBPACK FOOTER //
// ./src/trips/Trip.jsx